import { CRUD, CrudModel } from '../../classes'
import {
  AUTHORIZATION_STATUS,
  BaseModelData,
  INSURANCE_PROVIDER_LOOKUP_TYPE,
  InsuranceProviderLookup,
  InsuranceProviderLookupData,
  ModelDate,
  PATIENT_ACCOUNT_CLEARANCE_STATUS,
} from '..'
import { PatientInsuranceStatus } from './patient-insurance-status.enum'

export { PatientInsuranceStatus }

export enum PatientInsuranceType {
  PRIMARY = 'P',
  SECONDARY = 'S',
  TERTIARY = 'T',
  QUATERNARY = '4',
  QUINARY = '5',
  SENARY = '6',
}

/** X12 relationship codes; mirrors the API's `PatientRelationshipToSubscriber`. */
export enum PatientRelationshipToSubscriber {
  SELF = '18',
  SPOUSE = '01',
  CHILD = '19',
  OTHER = 'G8',
}

export enum PatientEligibilityStatus {
  NONE = 'N',
  HARD_FAIL = 'F',
  SOFT_FAIL = 'E',
  INVALID = 'IV',
  INACTIVE = 'I',
  ACTIVE = 'V',
  MISMATCH = 'M',
  NOT_REQUIRED = 'A',
  NO_CONNECTION = 'NC',
}

export interface PatientInsuranceData extends BaseModelData {
  patientId?: string
  accountIds?: string[]
  eid?: string
  carrierLookupId?: string
  clinicCarrierId?: string
  clinicCarrierName?: string
  customCarrierName?: string
  memberId?: string
  groupNumber?: string
  groupName?: string
  relationship?: PatientRelationshipToSubscriber | null
  /** The policy holder's name, when that is not the patient. Only usable as a pair. */
  subscriberFirstName?: string | null
  subscriberLastName?: string | null
  effectiveFrom?: ModelDate
  effectiveTo?: ModelDate
  status?: PatientInsuranceStatus
  authorizationStatus?: AUTHORIZATION_STATUS
  eligibilityStatus?: PatientEligibilityStatus
  type?: PatientInsuranceType
  lastVerifiedAt?: ModelDate
  lastVerifiedBy?: string
  /**
   * A person verified this coverage by hand because the carrier has no connectivity. While
   * set, the api holds `eligibilityStatus` at the recorded answer — ACTIVE or INACTIVE —
   * instead of resetting to NO_CONNECTION or re-deriving it from `status`.
   */
  manuallyVerified?: boolean
  manuallyVerifiedAt?: ModelDate
  manuallyVerifiedBy?: string
  eligibilityId?: string
  carrierId?: string
  lookupType?: INSURANCE_PROVIDER_LOOKUP_TYPE
  lookup?: InsuranceProviderLookupData
  isSelfPay?: boolean
  /** @deprecated Use clearanceStatuses instead */
  clearanceStatus?: PATIENT_ACCOUNT_CLEARANCE_STATUS
  clearanceStatuses?: Record<string, PATIENT_ACCOUNT_CLEARANCE_STATUS>
  /**
   * Per-account coverage role, keyed by account ID. `type` remains the patient-level
   * fallback; use getAccountType() to resolve the visit-correct role.
   */
  accountTypes?: Record<string, PatientInsuranceType>
}

@CRUD({
  base: '/insurances/policies',
})
export class PatientInsurance extends CrudModel implements PatientInsuranceData {
  patientId?: string
  accountIds?: string[]
  eid?: string
  carrierLookupId?: string
  clinicCarrierId?: string
  clinicCarrierName?: string
  customCarrierName?: string
  memberId?: string
  groupNumber?: string
  groupName?: string
  relationship?: PatientRelationshipToSubscriber | null
  subscriberFirstName?: string | null
  subscriberLastName?: string | null
  effectiveFrom?: string
  effectiveTo?: string
  status?: PatientInsuranceStatus
  authorizationStatus?: AUTHORIZATION_STATUS
  eligibilityStatus?: PatientEligibilityStatus
  type: PatientInsuranceType
  lastVerifiedAt?: moment.Moment
  lastVerifiedBy?: string
  manuallyVerified?: boolean
  manuallyVerifiedAt?: moment.Moment
  manuallyVerifiedBy?: string
  eligibilityId?: string
  carrierId?: string
  lookupType?: INSURANCE_PROVIDER_LOOKUP_TYPE
  lookup?: InsuranceProviderLookup
  isSelfPay?: boolean
  /** @deprecated Use clearanceStatuses instead */
  clearanceStatus?: PATIENT_ACCOUNT_CLEARANCE_STATUS
  clearanceStatuses?: Record<string, PATIENT_ACCOUNT_CLEARANCE_STATUS>
  accountTypes?: Record<string, PatientInsuranceType>

  constructor(data?: PatientInsuranceData) {
    super(data, true)

    // default type is primary
    this.type = data?.type ?? PatientInsuranceType.PRIMARY

    if (data?.lookup) {
      this.lookup = new InsuranceProviderLookup(data.lookup)
    }
  }

  // convert type to a number to allow policies to be sorted
  get typeIndex(): number {
    switch (this.type) {
      case PatientInsuranceType.PRIMARY: return 1
      case PatientInsuranceType.SECONDARY: return 2
      case PatientInsuranceType.TERTIARY: return 3
      case PatientInsuranceType.QUATERNARY: return 4
      case PatientInsuranceType.QUINARY: return 5
      case PatientInsuranceType.SENARY: return 6
      default: return 0
    }
  }

  get typeText(): string {
    return `insurances.type.${this.type}`
  }

  public getClearanceStatus(accountId: string): PATIENT_ACCOUNT_CLEARANCE_STATUS | undefined {
    if (this.clearanceStatuses) {
      return this.clearanceStatuses[accountId] ?? PATIENT_ACCOUNT_CLEARANCE_STATUS.NONE
    }

    return this.clearanceStatus
  }

  /**
   * `perVisitEnabled` must reflect the clinic's live flag - accountTypes can hold stale
   * data from when the flag was previously on, so its presence alone can't be read as
   * "flag is on" (see PIA-2124 precedent).
   */
  public getAccountType(accountId: string | null | undefined, perVisitEnabled: boolean): PatientInsuranceType {
    return (perVisitEnabled && accountId && this.accountTypes?.[accountId]) || this.type
  }

  /**
   * `manuallyVerified` is deliberately absent: sending it on every save would re-stamp - or,
   * on a stale copy, clear - a verification an unrelated edit never meant to touch.
   *
   * The subscriber fields distinguish `null` from `undefined`: the API applies the body with
   * `fromJSON`, and `JSON.stringify` drops `undefined`, so only an explicit `null` clears a
   * stored value while `undefined` leaves it untouched.
   */
  override toJSON() {
    return Object.assign(super.toJSON(), {
      patientId: this.patientId,
      eid: this.eid,
      carrierLookupId: this.carrierLookupId,
      clinicCarrierId: this.clinicCarrierId,
      clinicCarrierName: this.clinicCarrierName,
      memberId: this.memberId,
      groupName: this.groupName,
      groupNumber: this.groupNumber,
      relationship: this.relationship,
      subscriberFirstName: this.subscriberFirstName,
      subscriberLastName: this.subscriberLastName,
      effectiveFrom: this.effectiveFrom,
      effectiveTo: this.effectiveTo,
      status: this.status,
      authorizationStatus: this.authorizationStatus,
      eligibilityStatus: this.eligibilityStatus,
      clearanceStatuses: this.clearanceStatuses,
      type: this.type,
      lastVerifiedAt: this.lastVerifiedAt,
      lastVerifiedBy: this.lastVerifiedBy,
    })
  }
}
